import jsPDF from 'jspdf';

class PDFService {
  constructor() {
    this.doc = null;
    this.currentY = 20;
    this.pageHeight = 297; // A4 height in mm
    this.margin = 20;
    this.lineHeight = 6;
  }

  // Inicializa novo documento PDF
  initDocument() {
    this.doc = new jsPDF('p', 'mm', 'a4');
    this.currentY = 20;
    
    // Configurar fonte padrão
    this.doc.setFont('helvetica', 'normal');
  }

  // Adiciona quebra de página se necessário
  checkPageBreak(neededSpace = 10) {
    if (this.currentY + neededSpace > this.pageHeight - this.margin) {
      this.doc.addPage();
      this.currentY = this.margin;
      return true;
    }
    return false;
  }

  // Adiciona texto com quebra automática
  addText(text, fontSize = 11, style = 'normal', align = 'left') {
    this.doc.setFontSize(fontSize);
    this.doc.setFont('helvetica', style);
    
    const pageWidth = this.doc.internal.pageSize.getWidth();
    const textWidth = pageWidth - (this.margin * 2);
    
    const lines = this.doc.splitTextToSize(text, textWidth);
    
    this.checkPageBreak(lines.length * this.lineHeight);
    
    lines.forEach(line => {
      let x = this.margin;
      if (align === 'center') {
        x = pageWidth / 2;
      } else if (align === 'right') {
        x = pageWidth - this.margin;
      }
      
      this.doc.text(line, x, this.currentY, { align });
      this.currentY += this.lineHeight;
    });
    
    return this;
  }

  // Adiciona título
  addTitle(text, fontSize = 16) {
    this.checkPageBreak(10);
    this.addText(text, fontSize, 'bold', 'center');
    this.currentY += 5;
    return this;
  }

  // Adiciona subtítulo
  addSubtitle(text, fontSize = 14) {
    this.checkPageBreak(8);
    this.addText(text, fontSize, 'bold');
    this.currentY += 3;
    return this;
  }

  // Adiciona linha em branco
  addSpace(space = 5) {
    this.currentY += space;
    return this;
  }

  // Adiciona linha horizontal
  addLine() {
    this.checkPageBreak(5);
    const pageWidth = this.doc.internal.pageSize.getWidth();
    this.doc.line(this.margin, this.currentY, pageWidth - this.margin, this.currentY);
    this.currentY += 5;
    return this;
  }

  // Gera cabeçalho do contrato
  generateHeader(contractData) {
    this.addTitle('CONTRATO DE ' + this.getContractTypeName(contractData.contractType));
    this.addSpace(10);
    return this;
  }

  // Gera seção das partes
  generateParties(parties) {
    this.addSubtitle('PARTES CONTRATANTES');
    this.addSpace(3);

    // Primeira parte
    if (parties.party1) {
      const party1 = parties.party1;
      this.addText(`CONTRATANTE: ${party1.name}`, 11, 'bold');
      
      if (party1.type === 'PF') {
        this.addText(`CPF: ${party1.document}`);
        this.addText(`RG: ${party1.rg || 'Não informado'}`);
      } else {
        this.addText(`CNPJ: ${party1.document}`);
        this.addText(`Razão Social: ${party1.companyName || party1.name}`);
        this.addText(`Representante Legal: ${party1.representative || 'Não informado'}`);
      }
      
      this.addText(`Endereço: ${party1.address}, ${party1.neighborhood}, ${party1.city}/${party1.state}`);
      this.addText(`CEP: ${party1.cep}`);
      this.addText(`Telefone: ${party1.phone}`);
      this.addText(`Email: ${party1.email}`);
    }

    this.addSpace(8);

    // Segunda parte
    if (parties.party2) {
      const party2 = parties.party2;
      this.addText(`CONTRATADO: ${party2.name}`, 11, 'bold');
      
      if (party2.type === 'PF') {
        this.addText(`CPF: ${party2.document}`);
        this.addText(`RG: ${party2.rg || 'Não informado'}`);
      } else {
        this.addText(`CNPJ: ${party2.document}`);
        this.addText(`Razão Social: ${party2.companyName || party2.name}`);
        this.addText(`Representante Legal: ${party2.representative || 'Não informado'}`);
      }
      
      this.addText(`Endereço: ${party2.address}, ${party2.neighborhood}, ${party2.city}/${party2.state}`);
      this.addText(`CEP: ${party2.cep}`);
      this.addText(`Telefone: ${party2.phone}`);
      this.addText(`Email: ${party2.email}`);
    }

    this.addSpace(10);
    return this;
  }

  // Gera seção do imóvel
  generateProperty(property) {
    this.addSubtitle('OBJETO DO CONTRATO');
    this.addSpace(3);

    this.addText(`Tipo de Imóvel: ${this.getPropertyTypeName(property.type)}`, 11, 'bold');
    this.addText(`Endereço: ${property.address}, ${property.neighborhood}`);
    this.addText(`Cidade: ${property.city}/${property.state}`);
    this.addText(`CEP: ${property.cep}`);
    
    if (property.area) {
      this.addText(`Área: ${property.area}m²`);
    }
    
    if (property.bedrooms) {
      this.addText(`Quartos: ${property.bedrooms}`);
    }
    
    if (property.bathrooms) {
      this.addText(`Banheiros: ${property.bathrooms}`);
    }
    
    if (property.parkingSpaces) {
      this.addText(`Vagas de Garagem: ${property.parkingSpaces}`);
    }

    if (property.description) {
      this.addSpace(3);
      this.addText('Descrição:', 11, 'bold');
      this.addText(property.description);
    }

    this.addSpace(10);
    return this;
  }

  // Gera seção financeira
  generateFinancial(financial, contractType) {
    this.addSubtitle('CONDIÇÕES FINANCEIRAS');
    this.addSpace(3);

    if (contractType === 'rental') {
      this.addText(`Valor do Aluguel: R$ ${this.formatCurrency(financial.value)}`, 11, 'bold');
      
      if (financial.deposit) {
        this.addText(`Depósito Caução: R$ ${this.formatCurrency(financial.deposit)}`);
      }
      
      if (financial.advancePayment) {
        this.addText(`Pagamento Antecipado: R$ ${this.formatCurrency(financial.advancePayment)}`);
      }
      
      this.addText(`Vencimento: Todo dia ${financial.dueDate || '10'} de cada mês`);
      
    } else if (contractType === 'sale') {
      this.addText(`Valor de Venda: R$ ${this.formatCurrency(financial.value)}`, 11, 'bold');
      
      if (financial.downPayment) {
        this.addText(`Entrada: R$ ${this.formatCurrency(financial.downPayment)}`);
      }
      
      if (financial.installments && financial.installments > 1) {
        const installmentValue = (financial.value - (financial.downPayment || 0)) / financial.installments;
        this.addText(`Parcelamento: ${financial.installments}x de R$ ${this.formatCurrency(installmentValue)}`);
      }
    }

    this.addText(`Forma de Pagamento: ${this.getPaymentMethodName(financial.paymentMethod)}`);

    this.addSpace(10);
    return this;
  }

  // Gera cláusulas
  generateClauses(clauses, contractType) {
    this.addSubtitle('CLÁUSULAS CONTRATUAIS');
    this.addSpace(3);

    let clauseNumber = 1;

    // Cláusulas padrão baseadas no tipo de contrato
    const standardClauses = this.getStandardClauses(contractType);
    
    standardClauses.forEach(clause => {
      if (clauses.standardClauses && clauses.standardClauses[clause.id]) {
        this.addText(`${clauseNumber}ª - ${clause.title.toUpperCase()}`, 11, 'bold');
        this.addText(clause.content);
        this.addSpace(5);
        clauseNumber++;
      }
    });

    // Cláusulas personalizadas
    if (clauses.customClauses && clauses.customClauses.length > 0) {
      clauses.customClauses.forEach(customClause => {
        this.addText(`${clauseNumber}ª - CLÁUSULA ESPECIAL`, 11, 'bold');
        this.addText(customClause.content);
        this.addSpace(5);
        clauseNumber++;
      });
    }

    return this;
  }

  // Gera footer com assinaturas
  generateSignatures(parties) {
    this.addSpace(20);
    this.addLine();
    this.addSpace(10);

    const currentDate = new Date().toLocaleDateString('pt-BR');
    this.addText(`São Luís, ${currentDate}`, 11, 'normal', 'right');
    this.addSpace(20);

    // Linhas para assinatura
    const pageWidth = this.doc.internal.pageSize.getWidth();
    const signatureWidth = 60;
    const spacing = (pageWidth - (this.margin * 2) - (signatureWidth * 2)) / 3;

    this.checkPageBreak(30);

    // Primeira assinatura
    let xPos = this.margin;
    this.doc.line(xPos, this.currentY, xPos + signatureWidth, this.currentY);
    this.doc.text(parties.party1?.name || 'CONTRATANTE', xPos + (signatureWidth / 2), this.currentY + 5, { align: 'center' });

    // Segunda assinatura
    xPos += signatureWidth + spacing;
    this.doc.line(xPos, this.currentY, xPos + signatureWidth, this.currentY);
    this.doc.text(parties.party2?.name || 'CONTRATADO', xPos + (signatureWidth / 2), this.currentY + 5, { align: 'center' });

    return this;
  }

  // Métodos auxiliares
  getContractTypeName(type) {
    const types = {
      'rental': 'LOCAÇÃO',
      'sale': 'COMPRA E VENDA',
      'administration': 'ADMINISTRAÇÃO PREDIAL'
    };
    return types[type] || 'IMOBILIÁRIO';
  }

  getPropertyTypeName(type) {
    const types = {
      'apartment': 'Apartamento',
      'house': 'Casa',
      'commercial': 'Imóvel Comercial',
      'lot': 'Terreno',
      'farm': 'Fazenda/Sítio'
    };
    return types[type] || type;
  }

  getPaymentMethodName(method) {
    const methods = {
      'cash': 'À Vista',
      'installments': 'Parcelado',
      'bank_transfer': 'Transferência Bancária',
      'pix': 'PIX',
      'check': 'Cheque'
    };
    return methods[method] || method;
  }

  formatCurrency(value) {
    return new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  }

  getStandardClauses(contractType) {
    const clauses = {
      rental: [
        {
          id: 'rent_payment',
          title: 'DO PAGAMENTO DO ALUGUEL',
          content: 'O aluguel deverá ser pago mensalmente até o dia do vencimento, sem desconto ou abatimento, no valor estipulado neste contrato.'
        },
        {
          id: 'property_use',
          title: 'DO USO DO IMÓVEL',
          content: 'O imóvel destina-se exclusivamente ao uso residencial, sendo vedado o uso para fins comerciais sem prévia autorização do locador.'
        },
        {
          id: 'conservation',
          title: 'DA CONSERVAÇÃO',
          content: 'O locatário obriga-se a conservar o imóvel no estado em que o recebeu, responsabilizando-se por eventuais danos causados.'
        }
      ],
      sale: [
        {
          id: 'property_conditions',
          title: 'DO ESTADO DO IMÓVEL',
          content: 'O imóvel é vendido no estado em que se encontra, tendo o comprador pleno conhecimento de suas condições.'
        },
        {
          id: 'documentation',
          title: 'DA DOCUMENTAÇÃO',
          content: 'O vendedor obriga-se a fornecer toda a documentação necessária para a transferência da propriedade.'
        },
        {
          id: 'possession',
          title: 'DA POSSE',
          content: 'A posse do imóvel será transferida ao comprador mediante o pagamento integral do preço acordado.'
        }
      ],
      administration: [
        {
          id: 'services',
          title: 'DOS SERVIÇOS',
          content: 'A administradora prestará os serviços de administração predial conforme especificado neste contrato.'
        },
        {
          id: 'fees',
          title: 'DOS HONORÁRIOS',
          content: 'Os honorários da administração serão cobrados conforme percentual acordado sobre os valores arrecadados.'
        }
      ]
    };

    return clauses[contractType] || [];
  }

  // Método principal para gerar o PDF completo
  async generateContract(contractData) {
    try {
      this.initDocument();
      
      this.generateHeader(contractData);
      this.generateParties(contractData.parties);
      this.generateProperty(contractData.property);
      this.generateFinancial(contractData.financial, contractData.contractType);
      this.generateClauses(contractData.clauses, contractData.contractType);
      this.generateSignatures(contractData.parties);

      // Gerar e baixar o PDF
      const fileName = `contrato_${contractData.contractType}_${Date.now()}.pdf`;
      this.doc.save(fileName);
      
      return {
        success: true,
        fileName,
        message: 'Contrato gerado com sucesso!'
      };
      
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      return {
        success: false,
        error: error.message,
        message: 'Erro ao gerar o contrato'
      };
    }
  }
}

export default new PDFService();